/**
 * Maintained by Sandon
 */
import {genMap} from './utils'
// todo 缺乏对 twoWay 模式下 disabled 的支持，缺乏对halfCheckedKeys的支持（目前场景可用checkAncestorFn覆盖）
// todo simple 配置，点选时逻辑已经完成，初始化逻辑未完成，因为产品暂停，所以暂停开发了
/*
 * 思考：对树数据的改变，只需要如下两组方法就行：setCheckedKeys和updateCheckedMultiple/Single
 * setCheckedKeys和updateCheckedMultiple/Single都只是针对数据层面的操作，不包括业务层面的逻辑（比如抛出特定事件等）
 * setCheckedKeys：全量选中指定的值（且只选中指定值），其他的设置为初始状态（非选中）
 * updateCheckedMultiple/Single：某个节点状态改变时，更新上下游关联状态
 */

export const defaultProps = {
  key: 'id',
  label: 'label',
  children: 'children',
  disabled: 'disabled',
  isLeaf: 'isLeaf'
}

export const Modes = {
  'TWO_WAY': 'twoWay', // 双向模式
  'SEPARATE': 'separate', // 分离模式
  'DOWN': 'down' // 向下传递模式
}

let id = 0
export class Node {
  constructor (options) {
    this._isTreeNode = true
    this.id = id++
    this.options = options
    this.level = options.level || 0
    this.data = options.data
    this.parent = options.parent
    this.loaded = options.loaded || false
    this.loading = options.loading || false
    this.singleAndNotMustLeaf = options.singleAndNotMustLeaf || false
    this.props = options.props
    this.tree = options.tree
    // subTreeCheckedState 只在 'down/separate模式 + 懒加载 + 多选'场景才会用到，值有: undefined/tree/false
    // undefined 是默认值，表示未手动修改；true/false 表示是手动修改后的值
    // 只有节点loaded 为false时，值才会是true/false， 否则都为 undefined
    // down 模式，此值来自交互+初始化；separate 模式，此值只会来自于初始化
    this.subTreeCheckedState = options.subTreeCheckedState
    this.all = this.subTreeCheckedState === true // down/separate模式才会用到

    // 把data对象中的数据抽出来，并增加$treeNodeId
    const props = Object.assign({}, defaultProps, options.props)
    this.key = options.data && options.data[props['key']]
    if (!this.singleAndNotMustLeaf && this.level !== 0 && (this.key === null || this.key === undefined)) {
      throw new Error('key should be exist')
    }
    this.label = options.data && options.data[props['label']]
    this.completeLabel = this.parent && this.parent.completeLabel
      ? `${this.parent.completeLabel}${this.tree.labelDivider}${this.label}`
      : this.label
    this.childNodes = []
    this.disabled = this.tree.mode === Modes.TWO_WAY
      ? (options?.data && options?.data[props['disabled']]) || options?.parent?.disabled || false
      : (options?.data && options?.data[props['disabled']]) || false
    this.isLeaf = (options.data && options.data[props['isLeaf']]) || false

    this.data && (this.data.$treeNodeId = this.id)

    // 其他的属性
    this.checked = this.tree.mode === Modes.TWO_WAY
      ? this.parent?.checked
      : this.tree.mode === Modes.DOWN
        ? this.subTreeCheckedState
        : false
    this.indeterminate = false // 子孙中是否存在选中的项，true代表存在，否则false
  }

  // 基于子节点数据初始化children
  initChildren (dataArr, startIndex) {
    const parent = this
    parent.loaded = true
    parent.loading = false
    // parent.isLeaf = dataArr.length === 0

    dataArr.forEach(data => {
      const child = new Node({
        level: parent.level + 1,
        data,
        parent,
        props: parent.props,
        tree: parent.tree,
        subTreeCheckedState: parent.subTreeCheckedState
      })
      if (startIndex === undefined) {
        parent.childNodes.push(child)
      } else {
        parent.childNodes.splice(startIndex, 0, child)
        startIndex++
      }
    })
  
    parent.isLeaf = parent.childNodes.length === 0
    parent.subTreeCheckedState = undefined

    // 修改父级的 indeterminate
    parent.indeterminate = !!(parent.childNodes.find(_ => _.checked))
  }
  
  // 单选情况下更新节点的checked，场景：手动触发
  updateCheckedSingle (checked) {
    if (this.disabled) return
    this.checked = checked

    let node = this.parent
    while (node.parent) { // 不为根节点
      node.indeterminate = checked

      node = node.parent
    }
  }

  // 多选情况下更新节点的checked, 并更新上下游关联状态，场景：手动触发
  updateCheckedMultiple (checked = false, ignoreDisabled = false, halfCheckedKeys = []) {
    if (this.disabled) return
    const mode = this.tree.mode
    const node = this
    node.checked = checked

    // 注意顺序： 
    // 1、自己(checked, subTreeCheckedState, indeterminate) 
    // 2、子孙的上述3个值
    // 3、自己的 all (需要时)
    // 4、祖先的(checked, indeterminate，all) 
    
    // 1+2、修改 自己+子孙 的选择状态：checked, indeterminate, subTreeCheckedState
    // 只在非 separate 模式下需要执行，separate模式只需修改自身checked（上面已经修改了）
    // todo 如果将来 down 模式下不往下传递"取消选中"，则在此处理
    if (mode !== Modes.SEPARATE) {
      let arr = [node]
      for (let i = 0; i !== arr.length; i++) {
        const curNode = arr[i]
    
        // 设置 checked 和 subTreeCheckedState
        if (!curNode.disabled) {
          curNode.checked = checked
          if (this.tree.mode === Modes.DOWN && !curNode.loaded) {
            curNode.subTreeCheckedState = checked
          }
        }
    
        // 设置 indeterminate
        // 有disabled且checked => true
        // 否则 => 存在非disabled节点 && checked
        let existDisabledAndCheckedChild = false
        let existNonDisabledChild = false
        curNode.childNodes.forEach(child => {
          if (!child.disabled) {
            existNonDisabledChild = true
          } else if (child.checked) {
            existDisabledAndCheckedChild = true
          }
        })
        curNode.indeterminate = existDisabledAndCheckedChild || (existNonDisabledChild && checked)
    
        // 广度优先的遍历
        arr = arr.concat(arr[i].childNodes)
      }  
    }

    // 3、设置自己的all(需要时)
    if (this.tree.simple && (mode === Modes.SEPARATE || mode === Modes.DOWN)) {
      node.all = node.isLeaf
        ? node.checked
        : node.loaded
          ? node.checked && !node.childNodes.find(_ => !_.all)
          : node.checked && node.subTreeCheckedState
    }
    
    // 4、祖先的(checked, indeterminate，all) 
    let tmp = node.parent
    while (tmp) {
      // this.tree.isSelectedAncestor
      setBasedOnChildren(tmp, mode, null, [], this.tree.simple)

      tmp = tmp.parent
    }

    // 针对down 模式，修改 keysSubTreeChecked, 当前节点选中或取消选中都需要执行
    this.tree.removeKeysSubTree(this)
  }
  
  _setSubtreeProp (propName, propValue) {
    let arr = [this]
    for (let i = 0; i !== arr.length; i++) {
      const node = arr[i]
      node[propName] = propValue
    
      if (node.childNodes.length) {
        arr = arr.concat(node.childNodes)
      }
    }
  }
  
  // 对外接口 - 新增子节点
  appendChildren (dataArr, startIndex) {
    // 只有loaded了才能调用，因为当没有loaded时，外部可以直接在数据上添加一项，然后在展开时传入最新的数据即可
    if (!this.loaded) return
    if (!Array.isArray(dataArr)) {
      console.error('appendChildren 参数需为数组')
      return
    }
    if (!dataArr.length) return
    
    this.initChildren(dataArr, startIndex)
    
    this.tree.cbs.appendChildrenCb?.(this)
  }
  
  // 对外接口 - 当前节点之后插入新兄弟节点
  insertAfter (dataArr) {
    // if (!Array.isArray(dataArr) || !dataArr.length) return
    //
    // const index = this.parent.childNodes.findIndex(_ => _=== this)
    // if (index === -1) return
    // this.parent.initChildren(dataArr, index + 1)
    //
    // this.tree.cbs.appendChildrenCb?.(this)
  
    const index = this.parent.childNodes.findIndex(_ => _=== this)
    if (index === -1) return
    
    this.parent.appendChildren(dataArr, index + 1)
  }
  
  // 对外接口 - 当前节点之前插入新兄弟节点
  insertBefore (dataArr) {
    const index = this.parent.childNodes.findIndex(_ => _=== this)
    if (index === -1) return
  
    this.parent.appendChildren(dataArr, index)
  }
  
  // 对外接口 - 启用
  enable () {
    if (this.tree.mode === Modes.TWO_WAY) {
      // loop whole subtree to set disabled false
      !this.parent.disabled && this._setSubtreeProp('disabled', false)
    } else if (this.tree.mode === Modes.DOWN) {
      // loop whole subtree to set disabled false
      this._setSubtreeProp('disabled', false)
    } else {
      this.disabled = false
    }
  }
  
  // 对外接口 - 禁用
  disable () {
    if (this.tree.mode === Modes.TWO_WAY) {
      // loop whole subtree to set disabled true
      !this.parent.disabled && this._setSubtreeProp('disabled', true)
    } else if (this.tree.mode === Modes.DOWN) {
      // loop whole subtree to set disabled true
      this._setSubtreeProp('disabled', true)
    } else {
      this.disabled = true
    }
  }
  
  // 对外接口 - 展开节点
  async expand () {
    await this.tree.cbs.expandCb?.(this)
  }
}

export default class Tree {
  constructor (props, lazy, multiple, mode = Modes.TWO_WAY, checkAncestorFn, labelDivider, simple) {
    this.originProps = props
    this.props = Object.assign({}, defaultProps, props)
    this.lazy = lazy
    this.multiple = multiple
    this.mode = mode
    this.checkAncestorFn = checkAncestorFn
    this.keysSubTreeChecked = []
    this.labelDivider = labelDivider
    this.simple = simple
    this.cbs = {}
  
    this.root = new Node({
      level: 0,
      data: {},
      props,
      tree: this
    })

    // this.targetCheckedKeys = []
    // if (this.checkAncestorFn) {
    //   // 是已选中节点的严格祖先
    //   this.isSelectedAncestor = (node) => {
    //     const index = this.targetCheckedKeys.findIndex((key) => {
    //       return this.checkAncestorFn(node, key)
    //     })
    //     return index !== -1
    //   }
    // }
  }

  // 全量选中指定的值（且只选中指定值），其他的设置为初始状态（非选中）, 场景：初始化
  // 返回所有选中的 Nodes 以及未初始化的 keys
  setCheckedKeys (keys, force = false, halfCheckedKeys = []) {
    const mode = this.mode
    const root =  this.root
    const keysSetWithSubTreeChecked = this.lazy && this.multiple && this.mode === Modes.DOWN
      ? new Set(this.keysSubTreeChecked)
      : new Set([])
    const checkedNodes = []
    const keysSet = new Set(keys)
    root.checked = false

    // 1、设置 checked
    let nodes = [...root.childNodes]
    for (let i = 0; i !== nodes.length; i++) {
      const node = nodes[i]
      if (!node.disabled || force) {
        if (mode === Modes.TWO_WAY) { // '双向模式'
          node.checked = keysSet.has(node.key) || node.parent.checked
        } else if (mode === Modes.SEPARATE) { // '分离模式' todo
          node.checked = keysSet.has(node.key)
        } else if (mode === Modes.DOWN) { // '向下模式'
          if (keysSetWithSubTreeChecked.has(node.key)) {
            node.subTreeCheckedState = true
            keysSetWithSubTreeChecked.delete(node.key)
          }

          if (node.subTreeCheckedState === undefined) {
            node.checked = keysSet.has(node.key)
          } else {
            node.checked = node.subTreeCheckedState
          }
        }

        keysSet.delete(node.key)

        node.indeterminate = false // 设为初始值
      }
      nodes = nodes.concat(node.childNodes)
    }

    this.keysSubTreeChecked = [...keysSetWithSubTreeChecked]

    // keys 为空数组，相当于清空，到此就可以return了，无需执行如下操作提升性能
    if (!keys.length) return {checkedNodes, leftKeys: [...keysSet]}

    // 2、设置 indeterminate 及联动的checked，倒序遍历nodes，也即从下至上按层遍历树
    for (let i = nodes.length - 1; i !== -1; i--) {
      const node = nodes[i]

      const isSelectedAncestor = !this.checkAncestorFn ? null : (node) => {
        const index = keys.findIndex((key) => {
          return this.checkAncestorFn(node, key)
        })
        return index !== -1
      }
      setBasedOnChildren(node, mode, isSelectedAncestor, halfCheckedKeys, this.simple)

      // 收集 checkedNodes
      if (node.checked) {
        checkedNodes.push(node)
      }
    }

    return {checkedNodes: checkedNodes.reverse(), leftKeys: [...keysSet]}
  }

  // 过滤所有选取的项目，去掉父级已经被选中的项(可配置)
  refineNodes (selectedNodes) {
    const mode = this.mode
    if (mode === Modes.DOWN || mode === Modes.SEPARATE) {
      // 父子级不联动
      if (this.simple)
        return selectedNodes.filter(_ => !_.all || !_.parent.all)
      else
        return [...selectedNodes]
    } else {
      // 父子级联动
      const selectedNodesMap = genMap(selectedNodes, 'id')
      return selectedNodes.filter((treeNode) => {
        let parent = treeNode.parent
        return parent && !selectedNodesMap[parent.id]
      })
    }
  }

  // 基于key 获取节点
  getNodes (keys) {
    const keysSet = new Set(keys)
    const targetNodes = []
    let nodes = [...this.root.childNodes]
    for (let i = 0; i !== nodes.length && keysSet.size; i++) {
      const node = nodes[i]
      if (keysSet.has(node.key)) {
        targetNodes.push(node)
        keysSet.delete(node.key)
      }
      nodes = nodes.concat(node.childNodes)
    }

    return targetNodes
  }

  // 设置 keysSubTreeCheckedTrue
  // 这里对场景做了假设和要求：只会在初始化时（即在用户做任何操作之前）进行设置
  setKeysSubTreeChecked (keys) {
    this.keysSubTreeChecked = keys
  }

  // 对node取消选择后，删除 keysSubTreeChecked 中它的子孙
  removeKeysSubTree (node) {
    if (this.mode === Modes.DOWN && this.lazy && this.multiple && this.checkAncestorFn && !node.checked) {
      this.keysSubTreeChecked = this.keysSubTreeChecked.filter((key) => {
        return !this.checkAncestorFn(node, key)
      })
    }
  }
  
  getKeysSubTreeChecked () {
    return this.keysSubTreeChecked
  }
  
  // 设置并合并 cbs
  setCbs (cbs) {
    this.cbs = Object.assign(this.cbs, cbs)
  }
}

// 基于 children 的状态设置 indeterminate，all ，及联动来产生的 checked（TWO_WAY 模式下）
// 前提假设是： children 的 checked 和 subTreeCheckedState，之前已经设置好；并且是自下而上地逐层设置；
function setBasedOnChildren (node, mode = Modes.TWO_WAY, isSelectedAncestor, halfCheckedKeys = [], simple = false) {
  const childNodes = node.childNodes
  if (!childNodes.length) {
    // isSelectedAncestor 和 halfCheckedKeys 来设置 indeterminate
    if (isSelectedAncestor) {
      node.indeterminate = isSelectedAncestor(node)
    } else {
      node.indeterminate = halfCheckedKeys.indexOf(node.key) !== -1
    }
  } else {
    let strictCheckedCount = 0
    let indeterminate = false
    const len = childNodes.length
    for (let j = 0; j !== len; j++) {
      const childNode = childNodes[j]
      if (childNode.indeterminate) {
        indeterminate = true
      }
      if (childNode.checked) {
        strictCheckedCount++
      }
    }
    if (mode === Modes.TWO_WAY) { // '双向模式' todo 支持 disabled
      if (strictCheckedCount === len) {
        // 选中
        node.checked = true
        node.indeterminate = true // false, 由于选中和半选的样式分开了，所以改成true
      } else if (strictCheckedCount === 0 && !indeterminate) {
        // 未选
        node.checked = false
        node.indeterminate = false
      } else {
        // 半选状态
        node.checked = false
        node.indeterminate = true
      }
    } else if (mode === Modes.SEPARATE || mode === Modes.DOWN) { // '分离模式'和'向下模式'
      node.indeterminate = indeterminate || !!strictCheckedCount
      
      if (simple) {
        // 当前节点 all = true 的条件：
        // 1、如果节点是叶子节点，那么自己checked
        // 2、如果非叶子节点:
        //   2.1、如果节点loaded, 那么自己checked 并且 所有子节点都all=true(或者无子节点)
        //   2.2、如果节点非loaded，那么自己checked 并且 subTreeCheckedState === true
        node.all = node.isLeaf 
          ? node.checked
          : node.loaded
            ? node.checked && !childNodes.find(_ => !_.all)
            : node.checked && node.subTreeCheckedState
      }
    }

    // 还没设置上indeterminate，则基于 isSelectedAncestor 来设置
    if (isSelectedAncestor && !node.indeterminate) {
      node.indeterminate = isSelectedAncestor(node)
    }

    // 还没设置上indeterminate，则基于 halfCheckedKeys 来设置
    if (halfCheckedKeys.length && !node.indeterminate &&
      halfCheckedKeys.indexOf(node.key) !== -1) {
      node.indeterminate = true
    }
  }
}
